<script setup>
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import dayjs from 'dayjs';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import MultiLineChartCard from '@/app/socialListening/components/shared/MultiLineChartCard.vue';
import {
  POST_VOLUME_API_DATE_FORMAT,
  POST_VOLUME_GRAPH_OPTIONS,
  SOCIAL_LISTENING_DRAWERS,
  SOCIAL_LISTENING_REPORT_INTERACTION_TYPES,
  SOCIAL_LISTENING_REPORT_SECTIONS,
  TOPIC_COMPARISONS_TOOLTIPS,
} from '@/app/socialListening/constants';
import { getFormattedMultiMetersPostVolumeTimeSeriesChartData } from '@/app/socialListening/utils/formatters';
import { groupByTotalPostsPerPeriodWithPrimaryBreakdown } from '@/app/socialListening/utils/graph-utils';
import { socialListeningReportInteractionEvent } from '@/app/socialListening/utils/mixpanel-events';
import { DEFAULT_TIME_FORMAT, TIME_FORMATS } from '@/components/foundation/charts/chartjs.defaults';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';
import { scaleLabelFormatter } from '@/utils/formatters';

const comparisonStore = useSocialListeningComparisonsStore();
const topicsStore = useSocialListeningTopicsStore();
const { launchDrawer } = useDrawer();
const route = useRoute();

const chartCardProps = computed(() => ({
  title: 'Mentions Trend',
  titleTooltip: TOPIC_COMPARISONS_TOOLTIPS.MENTIONS_TREND,
}));

const topicsPostVolumeTimeSeriesMeters = computed(() => {
  const graphOptions = POST_VOLUME_GRAPH_OPTIONS.TOTAL_POSTS;
  const breakdownDataset = [];
  Object.keys(comparisonStore.topicsPostVolumeTimeSeriesPerChannel).forEach((topicName) => {
    const breakdownGroups = groupByTotalPostsPerPeriodWithPrimaryBreakdown(
      comparisonStore.topicsPostVolumeTimeSeriesPerChannel[topicName],
      [],
      graphOptions,
      [],
      comparisonStore.scale,
    );
    breakdownDataset.push({ ...breakdownGroups, label: topicName });
  });
  return getFormattedMultiMetersPostVolumeTimeSeriesChartData(breakdownDataset);
});

const mentionsTrendOptions = computed(() => {
  const { onOrAfter, onOrBefore } = comparisonStore.comparisonFilters.sourceCreated ?? {};
  return {
    scales: {
      x: {
        type: 'time',
        suggestedMin: dayjs(onOrAfter, POST_VOLUME_API_DATE_FORMAT).valueOf(),
        suggestedMax: dayjs(onOrBefore, POST_VOLUME_API_DATE_FORMAT).valueOf(),
        time: {
          tooltipFormat: TIME_FORMATS[comparisonStore.scale] || DEFAULT_TIME_FORMAT,
          unit: comparisonStore.scale?.toLowerCase(),
        },
      },
      y: {
        ticks: {
          callback: scaleLabelFormatter,
        },
      },
    },
    hover: {
      mode: 'nearest',
      intersect: false,
    },

    onClick: async (event, elements) => {
      let chartDatePoint = null;
      const selectedTopic = comparisonStore.selectedComparison.topics[elements[0].datasetIndex];
      if (elements.length > 0) {
        elements.forEach((element) => {
          const chartData =
            topicsPostVolumeTimeSeriesMeters.value[element.datasetIndex].data[element.index];

          if (!chartDatePoint) {
            const dateValue = chartData.x;
            chartDatePoint = dayjs(dateValue);
          }
        });
        const scaleUnit = comparisonStore.scale.toLowerCase();
        const startDate = chartDatePoint.startOf(scaleUnit).format(POST_VOLUME_API_DATE_FORMAT);
        const endDate = chartDatePoint.endOf(scaleUnit).format(POST_VOLUME_API_DATE_FORMAT);

        const { sourceCreated, keywordsAndHashtags, ...temporaryFiltersWithoutDate } =
          comparisonStore.temporaryFilters;

        comparisonStore.setDrawerSelectedTopic(selectedTopic);

        comparisonStore.setTempFilters({
          sourceCreated: {
            onOrBefore: endDate,
            onOrAfter: startDate,
          },
          keywordsAndHashtags:
            comparisonStore.drawerSelectedTopic?.searchBody.filters.keywordsAndHashtags,
          ...temporaryFiltersWithoutDate,
        });

        socialListeningReportInteractionEvent({
          reportType: SOCIAL_LISTENING_REPORT_SECTIONS.TOTAL_POSTS,
          interactionType: SOCIAL_LISTENING_REPORT_INTERACTION_TYPES.DATA_POINT_CLICKED,
          path: route.path,
          reportDetails: {
            ...comparisonStore.drawerSerializedFilters,
            keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
            topicId: selectedTopic.id,
          },
        });

        await launchDrawer({
          name: SOCIAL_LISTENING_DRAWERS.TOP_POST,
          props: {
            ...comparisonStore.drawerSerializedFilters,
            keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
            topicId: selectedTopic.id,
          },
        });
      }
    },
  };
});
</script>

<template>
  <MultiLineChartCard
    :chart-card-props="chartCardProps"
    :custom-chart-config="mentionsTrendOptions"
    :datasets="topicsPostVolumeTimeSeriesMeters"
    :loading="comparisonStore.pendingDataPull.topicsPostVolumePerChannel"
    show-default-legend
  />
</template>
